import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Monitor, Cpu, Clock, RefreshCw, Server, Wifi, Shield,
  CheckCircle2, AlertTriangle, ArrowLeft, ArrowUpRight,
  Power, Trash2, Key, Users, Lock, Unlock, Edit3, Send,
  Activity, Check, Layers, AlertCircle, Sparkles, Search,
  ChevronDown, Building2, Laptop
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../networking/axiosconfig';
import Navbar from './Navbar';

export default function BiometricMachineSettings() {
  const navigate = useNavigate();

  // State
  const [devices, setDevices] = useState([]);
  const [selectedDevId, setSelectedDevId] = useState(() => {
    return new URLSearchParams(window.location.search).get('dev_id') || null;
  });
  const [loading, setLoading] = useState(true);
  const [serverTime, setServerTime] = useState('');
  const [commands, setCommands] = useState([]);
  const [loadingCommands, setLoadingCommands] = useState(false);
  const [bannerMsg, setBannerMsg] = useState({ text: '', type: '' });

  // Scalable Device Search & Filter (handles 100+ devices)
  const [deviceSearch, setDeviceSearch] = useState('');

  // Form states
  const [customName, setCustomName] = useState('');
  const [serverIp, setServerIp] = useState('168.144.28.127');
  const [serverPort, setServerPort] = useState(4002);
  const [customDateTime, setCustomDateTime] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [syncingEmployees, setSyncingEmployees] = useState(false);

  const showBanner = (text, type = 'success') => {
    setBannerMsg({ text, type });
    setTimeout(() => setBannerMsg({ text: '', type: '' }), 6000);
  };

  /* ---------------- FETCH STATE ---------------- */
  const fetchState = useCallback(async (quiet = false) => {
    try {
      if (!quiet) setLoading(true);
      const res = await axiosInstance.get('/api/t700ui/state');
      const devs = res.data?.devices || [];
      setDevices(devs);
      if (res.data?.server_time) {
        setServerTime(res.data.server_time);
      }

      const urlDevId = new URLSearchParams(window.location.search).get('dev_id');
      if (devs.length > 0) {
        setSelectedDevId((prev) => {
          if (urlDevId && devs.some((d) => d.dev_id === urlDevId)) return urlDevId;
          if (prev && devs.some((d) => d.dev_id === prev)) return prev;
          return devs[0].dev_id;
        });
      }
    } catch (err) {
      console.error('Failed to load device state:', err);
      showBanner(err.response?.data?.error || err.message || 'Failed to load biometric machines', 'error');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  /* ---------------- FETCH COMMANDS ---------------- */
  const fetchCommands = useCallback(async (devId) => {
    if (!devId) return;
    try {
      setLoadingCommands(true);
      const res = await axiosInstance.get(`/api/t700ui/commands?dev=${devId}&limit=20`);
      setCommands(res.data || []);
    } catch (err) {
      console.warn('Failed to load command queue:', err.message);
    } finally {
      setLoadingCommands(false);
    }
  }, []);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  useEffect(() => {
    if (selectedDevId) {
      const current = devices.find((d) => d.dev_id === selectedDevId);
      if (current) {
        setCustomName(current.name || '');
        if (current.ip) {
          // If device has registered IP
        }
      }
      fetchCommands(selectedDevId);
    }
  }, [selectedDevId, devices, fetchCommands]);

  // Periodic polling for commands and live status (every 8s)
  useEffect(() => {
    const timer = setInterval(() => {
      if (selectedDevId) {
        fetchState(true);
        fetchCommands(selectedDevId);
      }
    }, 8000);
    return () => clearInterval(timer);
  }, [selectedDevId, fetchState, fetchCommands]);

  const activeDevice = devices.find((d) => d.dev_id === selectedDevId) || devices[0] || null;

  /* ---------------- SEND COMMAND HELPER ---------------- */
  const sendCommand = async (cmdCode, param = {}, successMsg = '') => {
    if (!selectedDevId) return;
    try {
      setActionLoading(true);
      const res = await axiosInstance.post('/api/t700ui/command', {
        dev_id: selectedDevId,
        cmd_code: cmdCode,
        param
      });
      showBanner(
        successMsg || `Command "${cmdCode}" queued successfully (#${res.data?.id}). Device will execute within ~10-20s.`,
        'success'
      );
      fetchCommands(selectedDevId);
    } catch (err) {
      console.error(`Command ${cmdCode} failed:`, err);
      showBanner(err.response?.data?.error || err.message || `Command ${cmdCode} failed`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  /* ---------------- ACTION HANDLERS ---------------- */
  // 1. Sync Machine Time with Server
  const handleSyncTime = () => {
    sendCommand('SET_TIME', {}, '⚡ Time synchronization command queued! Machine clock will match cloud server.');
  };

  // 1b. Custom Time Set
  const handleSetCustomTime = () => {
    if (!customDateTime) return alert('Please select date and time');
    const formatted = customDateTime.replace('T', ' ') + ':00';
    sendCommand('SET_TIME', { time: formatted }, `Machine time set to ${formatted} queued.`);
  };

  // 2. Rename Device
  const handleSaveName = () => {
    if (!customName.trim()) return alert('Please enter a device name');
    sendCommand('SET_FK_NAME', { fk_name: customName.trim().slice(0, 24) }, `Machine name updated to "${customName.trim()}".`);
  };

  // 3. Update Cloud Web Server Address
  const handleSaveServerInfo = () => {
    const ip = serverIp.trim();
    const port = Number(serverPort);
    if (!/^\d+\.\d+\.\d+\.\d+$/.test(ip) || !port) {
      return alert('Please enter a valid IPv4 address and numeric port');
    }
    if (
      !window.confirm(
        `Are you sure you want to redirect machine to ${ip}:${port}? If incorrect, machine will disconnect from cloud.`
      )
    )
      return;
    sendCommand('SET_WEB_SERVER_INFO', { server_ip: ip, server_port: port }, `Server address updated to ${ip}:${port}.`);
  };

  // 4. Door & Relay Control
  const handleDoorControl = (status) => {
    const isUnlock = status === 'on';
    sendCommand(
      'SET_DOOR_STATUS',
      { status },
      isUnlock ? '🔓 Door unlock pulse (5 seconds) sent to machine relay!' : '🔒 Lock door command sent.'
    );
  };

  // 5. Zero-Touch Maintenance Actions
  const handleRebootDevice = () => {
    if (!window.confirm('Restart the physical biometric machine? It will briefly go offline for ~1 minute.')) return;
    sendCommand('RESET_FK', {}, '🔄 Reboot command sent! Device will restart momentarily.');
  };

  const handleRefreshStatus = () => {
    sendCommand('GET_DEVICE_STATUS', {}, 'Hardware status refresh requested from device.');
  };

  const handleClearLogs = () => {
    if (
      !window.confirm(
        'Are you sure you want to clear attendance records from device memory? Ensure punches are synced first!'
      )
    )
      return;
    sendCommand('CLEAR_LOG_DATA', {}, 'Attendance log clear command queued on device.');
  };

  const handleClearAdminLock = () => {
    if (
      !window.confirm(
        'Clear all Manager/Admin passwords from machine keypad? All users on the machine will become normal users so you can access the device menu.'
      )
    )
      return;
    sendCommand('CLEAR_ALL_MANAGER', {}, 'Manager privileges cleared! Device keypad menu is now unlocked.');
  };

  // 6. Push All Organization Staff to Machine
  const handleSyncAllEmployees = async () => {
    try {
      setSyncingEmployees(true);
      const [syncRes, reqRes] = await Promise.all([
        axiosInstance.post('/api/organization/sync-machines'),
        axiosInstance.post('/api/t700ui/users/sync', { dev_id: selectedDevId }).catch(() => null)
      ]);
      if (syncRes.data?.status) {
        showBanner('All active employees have been queued for biometric sync! Device will auto-provision them.', 'success');
        fetchCommands(selectedDevId);
      } else {
        showBanner(syncRes.data?.message || 'Sync failed', 'error');
      }
    } catch (err) {
      showBanner(err.response?.data?.message || err.message || 'Error syncing employees', 'error');
    } finally {
      setSyncingEmployees(false);
    }
  };

  // Cancel pending command
  const handleCancelCommand = async (cmdId) => {
    try {
      await axiosInstance.post('/api/t700ui/command/cancel', { id: cmdId });
      showBanner('Command cancelled.', 'success');
    } catch (err) {
      showBanner(err.response?.data?.error || 'Failed to cancel command', 'error');
    }
  };

  const handleSelectDevice = (id) => {
    setSelectedDevId(id);
    const url = new URL(window.location);
    url.searchParams.set('dev_id', id);
    window.history.replaceState({}, '', url);
  };

  const filteredDevices = useMemo(() => {
    if (!deviceSearch.trim()) return devices;
    const q = deviceSearch.toLowerCase().trim();
    return devices.filter((d) =>
      d.dev_id?.toLowerCase().includes(q) ||
      d.name?.toLowerCase().includes(q) ||
      d.ip?.toLowerCase().includes(q) ||
      d.org_name?.toLowerCase().includes(q)
    );
  }, [devices, deviceSearch]);

  const userRole = Number(localStorage.getItem('user_role'));
  const statusObj = activeDevice?.status || {};
  const isOnline = activeDevice?.online;

  return (
    <div className="min-h-screen bg-slate-50/70 pb-16">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* TOP BREADCRUMB & HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-1">
              {userRole === 0 ? (
                <button
                  onClick={() => navigate('/saas-clients')}
                  className="hover:text-blue-600 transition flex items-center gap-1 font-bold text-blue-600"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  SaaS Fleet & Clients
                </button>
              ) : (
                <button
                  onClick={() => navigate('/manage-employees')}
                  className="hover:text-blue-600 transition flex items-center gap-1 font-medium"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Employees
                </button>
              )}
              <span>/</span>
              <span className="text-slate-800 font-bold">Biometric Cloud Settings</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3">
              <Monitor className="w-8 h-8 text-blue-600" />
              Biometric Hardware Remote Settings
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Complete zero-touch cloud control: Configure machine clock, IP/port, device name, door relay, and remote reboot without touching physical buttons.
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={handleSyncAllEmployees}
              disabled={syncingEmployees || !activeDevice}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md transition disabled:opacity-50"
              title="Push all employees to biometric machines"
            >
              <RefreshCw className={`w-4 h-4 ${syncingEmployees ? 'animate-spin' : ''}`} />
              {syncingEmployees ? 'Syncing...' : 'Sync All Staff to Machine'}
            </button>

            <a
              href={selectedDevId ? `/t700.html?dev_id=${selectedDevId}` : '/t700.html'}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 font-bold text-xs shadow-sm transition"
              title="Open Full Hardware Terminal & Raw Logs"
            >
              <span>Live Console</span>
              <ArrowUpRight className="w-3.5 h-3.5 text-slate-400" />
            </a>
          </div>
        </div>

        {/* NOTIFICATION BANNER */}
        {bannerMsg.text && (
          <div
            className={`p-4 rounded-2xl mb-6 text-xs sm:text-sm font-semibold flex items-center justify-between shadow-sm animate-fadeIn ${
              bannerMsg.type === 'error'
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-emerald-50 text-emerald-900 border border-emerald-200'
            }`}
          >
            <div className="flex items-center gap-2.5">
              {bannerMsg.type === 'error' ? (
                <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              )}
              <span>{bannerMsg.text}</span>
            </div>
            <button
              onClick={() => setBannerMsg({ text: '', type: '' })}
              className="text-slate-400 hover:text-slate-600 text-xs px-2 py-1"
            >
              ✕
            </button>
          </div>
        )}

        {/* LOADING STATE */}
        {loading ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-200">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-700">Connecting to Biometric Hardware Fleet...</p>
            <p className="text-xs text-slate-400 mt-1">Retrieving machine states and cloud socket parameters</p>
          </div>
        ) : devices.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-200 shadow-sm">
            <Cpu className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-slate-800">No Biometric Machines Provisioned</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto mt-1 mb-6">
              There are no physical biometric machines bound to your company organization yet. Connect a T700 or T800 device to port 4002 or contact Platform SuperAdmin.
            </p>
            <button
              onClick={() => navigate('/saas-clients')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white font-bold text-xs hover:bg-blue-500 transition shadow-sm"
            >
              <Cpu className="w-4 h-4" />
              View Hardware Fleet Console
            </button>
          </div>
        ) : (
          <>
            {/* DEVICE SELECTOR & ACTIVE HARDWARE HERO CARD */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-7 mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-slate-100">
                {/* Scalable Device Selector (Handles 1 to 100+ devices seamlessly) */}
                <div className="flex-1 min-w-[280px] max-w-2xl">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Select Biometric Device ({devices.length} Total)
                    </label>
                    <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500">
                      <span className="inline-flex items-center gap-1 text-emerald-600">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        {devices.filter((d) => d.online).length} Live
                      </span>
                      <span>•</span>
                      <span className="inline-flex items-center gap-1 text-slate-400">
                        <span className="w-2 h-2 rounded-full bg-slate-400" />
                        {devices.filter((d) => !d.online).length} Standby
                      </span>
                    </div>
                  </div>

                  {/* Search Filter for 5+ devices */}
                  {devices.length > 5 && (
                    <div className="relative mb-2">
                      <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search 100+ devices by ID, Name, IP, or Client..."
                        value={deviceSearch}
                        onChange={(e) => setDeviceSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 text-xs text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-hidden transition"
                      />
                    </div>
                  )}

                  {/* Primary Enterprise Dropdown (Never wraps or overflows 100 devices across page) */}
                  <div className="relative">
                    <select
                      value={selectedDevId || ''}
                      onChange={(e) => handleSelectDevice(e.target.value)}
                      className="w-full appearance-none bg-slate-50 hover:bg-slate-100/90 border border-slate-200 text-slate-900 font-bold text-xs sm:text-sm rounded-2xl py-2.5 pl-4 pr-10 focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-hidden transition cursor-pointer shadow-2xs"
                    >
                      {filteredDevices.map((d) => (
                        <option key={d.dev_id} value={d.dev_id}>
                          {d.online ? '🟢 [ONLINE]' : '⚪ [STANDBY]'} {d.name || d.model || 'Device'} (#{d.dev_id}) {d.org_name ? `• ${d.org_name}` : '• In Stock'} {d.ip ? `• IP: ${d.ip}` : ''}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>

                  {/* Quick-switch chips for <= 5 machines */}
                  {devices.length <= 5 && (
                    <div className="flex flex-wrap gap-1.5 mt-2.5">
                      {devices.map((d) => {
                        const isSel = d.dev_id === selectedDevId;
                        return (
                          <button
                            key={d.dev_id}
                            type="button"
                            onClick={() => handleSelectDevice(d.dev_id)}
                            className={`flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-bold transition border ${
                              isSel
                                ? 'bg-blue-600 text-white border-blue-600 shadow-2xs'
                                : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                            }`}
                          >
                            <span
                              className={`w-2 h-2 rounded-full ${
                                d.online ? 'bg-emerald-400 animate-pulse' : 'bg-slate-400'
                              }`}
                            />
                            <span>{d.name || d.model || 'Device'}</span>
                            <span className={`font-mono text-[10px] ${isSel ? 'text-blue-100' : 'text-slate-400'}`}>
                              #{d.dev_id.slice(-6)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Live Heartbeat & Online Status */}
                <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-3.5 h-3.5 rounded-full ${
                        isOnline ? 'bg-emerald-500 shadow-lg shadow-emerald-500/50 animate-pulse' : 'bg-slate-400'
                      }`}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-900">
                          {isOnline ? 'Cloud Push Connected' : 'Machine Standby / Offline'}
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                          {activeDevice?.model || 'T700'}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 font-mono mt-0.5">
                        Heartbeat: {activeDevice?.last_seen || 'No heartbeat yet'} • Firmware: v{activeDevice?.firmware || '1.0'}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={handleRefreshStatus}
                    disabled={actionLoading}
                    className="p-2 rounded-xl bg-white border border-slate-200 text-slate-600 hover:text-blue-600 hover:border-blue-300 transition shadow-xs"
                    title="Refresh Live Status"
                  >
                    <RefreshCw className={`w-4 h-4 ${actionLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Hardware Capacity & Memory Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mt-6">
                <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Enrolled Users</span>
                  <span className="text-lg font-bold text-slate-900 mt-0.5 block">
                    {statusObj.user_count ?? activeDevice?.user_count ?? 0}
                    <span className="text-xs font-normal text-slate-400 ml-1">/ {statusObj.user_max || 1000}</span>
                  </span>
                </div>

                <div className="p-3.5 rounded-2xl bg-blue-50/50 border border-blue-100">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 block">Fingerprints</span>
                  <span className="text-lg font-bold text-blue-950 mt-0.5 block">
                    {statusObj.fp_count ?? 0}
                    <span className="text-xs font-normal text-blue-400 ml-1">/ {statusObj.fp_max || 3000}</span>
                  </span>
                </div>

                <div className="p-3.5 rounded-2xl bg-emerald-50/50 border border-emerald-100">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 block">Smart Cards</span>
                  <span className="text-lg font-bold text-emerald-950 mt-0.5 block">
                    {statusObj.idcard_count ?? 0}
                    <span className="text-xs font-normal text-emerald-400 ml-1">/ {statusObj.idcard_max || 1000}</span>
                  </span>
                </div>

                <div className="p-3.5 rounded-2xl bg-purple-50/50 border border-purple-100">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-purple-600 block">Machine PINs</span>
                  <span className="text-lg font-bold text-purple-950 mt-0.5 block">
                    {statusObj.password_count ?? 0}
                    <span className="text-xs font-normal text-purple-400 ml-1">/ {statusObj.password_max || 1000}</span>
                  </span>
                </div>

                <div className="p-3.5 rounded-2xl bg-amber-50/50 border border-amber-100">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 block">Punch Logs</span>
                  <span className="text-lg font-bold text-amber-950 mt-0.5 block">
                    {statusObj.all_log_count ?? activeDevice?.log_count ?? 0}
                    <span className="text-xs font-normal text-amber-400 ml-1">punches</span>
                  </span>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Device IP</span>
                  <span className="font-mono text-sm font-bold text-slate-800 mt-1 block truncate">
                    {activeDevice?.ip || 'Pending Connection'}
                  </span>
                </div>

                <div className="p-3.5 rounded-2xl bg-blue-50/70 border border-blue-200 shadow-2xs">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 block flex items-center gap-1">
                    <Server className="w-3 h-3 text-blue-600" />
                    Cloud Server
                  </span>
                  <span className="font-mono text-xs sm:text-sm font-bold text-blue-950 mt-1 block truncate">
                    168.144.28.127:4002
                  </span>
                </div>
              </div>
            </div>

            {/* MAIN SETTINGS GRID: 2 COLUMNS */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* SETTING 1: MACHINE CLOCK & TIME SYNC */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-7 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                      <Clock className="w-5 h-5 text-blue-600" />
                      Machine Clock & Time Synchronization
                    </h3>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                      SET_TIME
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-6">
                    Biometric punches rely on precise hardware clock. Keep machine in sync with cloud IST server time so attendance timestamps are 100% accurate.
                  </p>

                  <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 mb-5 flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                        Cloud Server Time (Asia/Kolkata)
                      </span>
                      <span className="font-mono font-bold text-slate-800 text-base mt-0.5 block">
                        {serverTime || new Date().toLocaleString('en-IN')}
                      </span>
                    </div>

                    <button
                      onClick={handleSyncTime}
                      disabled={actionLoading}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-sm transition disabled:opacity-50"
                    >
                      <Sparkles className="w-4 h-4" />
                      Sync Server Time Now
                    </button>
                  </div>

                  {/* Custom Time Option */}
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1.5">
                      Or Set Custom Date & Time
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="datetime-local"
                        value={customDateTime}
                        onChange={(e) => setCustomDateTime(e.target.value)}
                        className="flex-1 px-3.5 py-2 rounded-xl border border-slate-300 text-xs font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        onClick={handleSetCustomTime}
                        disabled={actionLoading || !customDateTime}
                        className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs transition border border-slate-200 disabled:opacity-40"
                      >
                        Set Time
                      </button>
                    </div>
                  </div>
                </div>

                <p className="text-[11px] text-slate-400 mt-5 pt-4 border-t border-slate-100">
                  ⚡ When clicked, command is dispatched to device. The hardware updates its internal RTC quartz clock on the next poll (~15-20s).
                </p>
              </div>

              {/* SETTING 2: DEVICE NAME / IDENTITY */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-7 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                      <Edit3 className="w-5 h-5 text-indigo-600" />
                      Device Display Name & Label
                    </h3>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                      SET_FK_NAME
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-6">
                    Change the friendly name displayed on the machine screen and in reports (e.g. &quot;Main Entrance&quot;, &quot;Warehouse Gate&quot;, &quot;Floor 2&quot;).
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1.5">
                        Biometric Machine Name
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={customName}
                          onChange={(e) => setCustomName(e.target.value)}
                          maxLength={24}
                          placeholder="e.g. Main Gate T700"
                          className="flex-1 px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm font-semibold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <button
                          onClick={handleSaveName}
                          disabled={actionLoading}
                          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs shadow-sm transition"
                        >
                          <Send className="w-3.5 h-3.5" />
                          Save Name
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1">Maximum 24 alphanumeric characters.</p>
                    </div>

                    <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-600">
                      <span className="font-bold text-slate-800">Hardware Serial (dev_id): </span>
                      <span className="font-mono">{activeDevice?.dev_id}</span>
                    </div>
                  </div>
                </div>

                <p className="text-[11px] text-slate-400 mt-5 pt-4 border-t border-slate-100">
                  Renaming updates both the cloud panel database and the physical machine banner.
                </p>
              </div>

              {/* SETTING 3: CLOUD WEB SERVER CONFIGURATION */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-7 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                      <Server className="w-5 h-5 text-emerald-600" />
                      Cloud Server Connection (Zero Touch)
                    </h3>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                      SET_WEB_SERVER_INFO
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-6">
                    Remotely reconfigure the server IP and port that the biometric machine communicates with. You do not need to enter the device keypad menu.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold text-slate-700 block mb-1">
                        Cloud Server IP / Host
                      </label>
                      <input
                        type="text"
                        value={serverIp}
                        onChange={(e) => setServerIp(e.target.value)}
                        placeholder="168.144.28.127"
                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 font-mono text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">
                        Push Port
                      </label>
                      <input
                        type="number"
                        value={serverPort}
                        onChange={(e) => setServerPort(e.target.value)}
                        placeholder="4002"
                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 font-mono text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleSaveServerInfo}
                    disabled={actionLoading}
                    className="w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm transition"
                  >
                    Update Server Address on Machine
                  </button>
                </div>

                <div className="mt-5 pt-4 border-t border-slate-100 flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 p-2.5 rounded-xl">
                  <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
                  <span>
                    <strong>Caution:</strong> If an incorrect IP or port is provided, the machine will lose connection with this server and must be re-calibrated on the physical device keypad.
                  </span>
                </div>
              </div>

              {/* SETTING 4: ACCESS CONTROL & DOOR RELAY */}
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-7 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                      <Unlock className="w-5 h-5 text-purple-600" />
                      Access Control & Door Relay
                    </h3>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                      SET_DOOR_STATUS
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-6">
                    If your T700 / T800 is connected to an electromagnetic door lock or turnstile gate relay, trigger cloud unlock or test relay functionality here.
                  </p>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <button
                      onClick={() => handleDoorControl('on')}
                      disabled={actionLoading}
                      className="p-4 rounded-2xl border border-emerald-200 bg-emerald-50/70 hover:bg-emerald-100/70 text-emerald-950 font-bold text-xs flex flex-col items-center gap-2 transition"
                    >
                      <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-sm">
                        <Unlock className="w-5 h-5" />
                      </div>
                      <span>Unlock Door (5s Pulse)</span>
                      <span className="text-[10px] text-emerald-700 font-normal">Momentary relay pulse</span>
                    </button>

                    <button
                      onClick={() => handleDoorControl('off')}
                      disabled={actionLoading}
                      className="p-4 rounded-2xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-800 font-bold text-xs flex flex-col items-center gap-2 transition"
                    >
                      <div className="w-10 h-10 rounded-xl bg-slate-700 text-white flex items-center justify-center shadow-sm">
                        <Lock className="w-5 h-5" />
                      </div>
                      <span>Lock Door / Normal</span>
                      <span className="text-[10px] text-slate-500 font-normal">Access control active</span>
                    </button>
                  </div>
                </div>

                <p className="text-[11px] text-slate-400 mt-5 pt-4 border-t border-slate-100">
                  Ideal for receptionist desks or remote door opening without a physical buzzer switch.
                </p>
              </div>
            </div>

            {/* SECTION 5: DANGER ZONE & ZERO-TOUCH RECOVERY */}
            <div className="bg-white rounded-3xl border border-red-100 shadow-sm p-6 sm:p-7 mb-8">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-600" />
                  Zero-Touch Maintenance & Device Recovery
                </h3>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-800">
                  Admin Maintenance
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-6">
                Avoid standing in front of the machine and pressing keypad buttons. Perform maintenance operations remotely:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* 1. Remote Reboot */}
                <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50/60 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 font-bold text-slate-800 text-sm mb-1">
                      <Power className="w-4 h-4 text-blue-600" />
                      Restart Machine
                    </div>
                    <p className="text-[11px] text-slate-500 mb-4">
                      Reboots the physical machine operating system cleanly. Resolves temporary network lags.
                    </p>
                  </div>
                  <button
                    onClick={handleRebootDevice}
                    disabled={actionLoading}
                    className="w-full py-2 px-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs transition"
                  >
                    Reboot Device
                  </button>
                </div>

                {/* 2. Sync Staff */}
                <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50/60 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 font-bold text-slate-800 text-sm mb-1">
                      <Users className="w-4 h-4 text-emerald-600" />
                      Sync All Staff
                    </div>
                    <p className="text-[11px] text-slate-500 mb-4">
                      Transfers all registered company employees with cards & PINs to device memory.
                    </p>
                  </div>
                  <button
                    onClick={handleSyncAllEmployees}
                    disabled={syncingEmployees}
                    className="w-full py-2 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs transition"
                  >
                    Sync Employees
                  </button>
                </div>

                {/* 3. Clear Admin Locks */}
                <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50/60 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 font-bold text-slate-800 text-sm mb-1">
                      <Key className="w-4 h-4 text-amber-600" />
                      Clear Admin Locks
                    </div>
                    <p className="text-[11px] text-slate-500 mb-4">
                      Forgot machine menu password? Clears manager lock so anyone can access keypad menu.
                    </p>
                  </div>
                  <button
                    onClick={handleClearAdminLock}
                    disabled={actionLoading}
                    className="w-full py-2 px-3 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs transition"
                  >
                    Unlock Device Menu
                  </button>
                </div>

                {/* 4. Clear Log Records */}
                <div className="p-4 rounded-2xl border border-red-200 bg-red-50/40 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 font-bold text-red-900 text-sm mb-1">
                      <Trash2 className="w-4 h-4 text-red-600" />
                      Clear Punch Records
                    </div>
                    <p className="text-[11px] text-red-700/80 mb-4">
                      Frees up internal memory on device. Cloud copies in our database will remain safe.
                    </p>
                  </div>
                  <button
                    onClick={handleClearLogs}
                    disabled={actionLoading}
                    className="w-full py-2 px-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-bold text-xs transition shadow-xs"
                  >
                    Clear Machine Logs
                  </button>
                </div>
              </div>
            </div>

            {/* SECTION 6: LIVE HARDWARE COMMAND QUEUE & AUDIT TRAIL */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-7">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-4 border-b border-slate-100">
                <div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                      <Activity className="w-5 h-5 text-blue-600" />
                      Live Cloud Command Queue & Audit History
                    </h3>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 font-mono text-[11px] font-bold border border-blue-200">
                      <Server className="w-3 h-3 text-blue-600" />
                      Target Server: 168.144.28.127:4002
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Real-time status of all commands sent to machine #{selectedDevId}. Devices poll server port 4002 every ~15-20 seconds.
                  </p>
                </div>

                <button
                  onClick={() => fetchCommands(selectedDevId)}
                  disabled={loadingCommands}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${loadingCommands ? 'animate-spin' : ''}`} />
                  Refresh Queue
                </button>
              </div>

              {commands.length === 0 ? (
                <div className="p-8 text-center text-xs text-slate-400 bg-slate-50 rounded-2xl">
                  No recent commands dispatched to this device.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                        <th className="py-2.5 px-3">Cmd ID</th>
                        <th className="py-2.5 px-3">Command</th>
                        <th className="py-2.5 px-3">Target Server:Port</th>
                        <th className="py-2.5 px-3">Parameters</th>
                        <th className="py-2.5 px-3">Status</th>
                        <th className="py-2.5 px-3">Dispatched</th>
                        <th className="py-2.5 px-3">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {commands.map((c) => {
                        const statusColors = {
                          done: 'bg-emerald-100 text-emerald-800',
                          pending: 'bg-amber-100 text-amber-800 animate-pulse',
                          sent: 'bg-blue-100 text-blue-800',
                          cancelled: 'bg-slate-100 text-slate-600',
                          failed: 'bg-red-100 text-red-800'
                        };
                        return (
                          <tr key={c.id} className="hover:bg-slate-50/70 transition">
                            <td className="py-2.5 px-3 font-mono font-bold text-slate-700">
                              #{c.id}
                            </td>
                            <td className="py-2.5 px-3 font-mono font-semibold text-slate-900">
                              {c.cmd_code}
                            </td>
                            <td className="py-2.5 px-3 font-mono text-[11px] text-blue-700 font-semibold">
                              168.144.28.127:4002
                            </td>
                            <td className="py-2.5 px-3 font-mono text-slate-500 truncate max-w-xs">
                              {c.param ? JSON.stringify(c.param) : '-'}
                            </td>
                            <td className="py-2.5 px-3">
                              <span
                                className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                                  statusColors[c.status] || 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {c.status}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-slate-400 font-mono">
                              {c.created || '-'}
                            </td>
                            <td className="py-2.5 px-3">
                              {c.status === 'pending' && (
                                <button
                                  onClick={() => handleCancelCommand(c.id)}
                                  className="text-red-600 hover:text-red-800 font-semibold text-[11px]"
                                >
                                  Cancel
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
